<template>
  <v-row dense class="table-wrapper">
    <v-col cols="12">
      <v-row dense v-if="verified">
        <v-col cols="12">
          <v-row dense class="pb-2">
            <v-col cols="12">
              <v-btn outlined @click="handleOpenDashboard">
                <v-icon left>mdi-arrow-left</v-icon>
                Open dashboard
              </v-btn>
            </v-col>
          </v-row>
          <v-stepper non-linear v-model="currentStepNumber">
            <v-stepper-header>
              <template v-for="stepNumber in stepsLength">
                <v-stepper-step
                  edit-icon="$complete"
                  :color="
                    StepColorsByStatusMap[
                      stepsList[stepNumber - 1].status ||
                        stepsList[stepNumber - 1].defaultStatus
                    ]
                  "
                  :complete="checkIsStepCompleted(stepsList[stepNumber - 1])"
                  :editable="checkIsStepSigned(stepsList[stepNumber - 1])"
                  :key="`${stepNumber}-step`"
                  :step="stepNumber"
                >
                  {{ stepsList[stepNumber - 1].step }}
                </v-stepper-step>

                <v-divider
                  v-if="stepNumber !== stepsLength"
                  :key="stepNumber"
                />
              </template>
            </v-stepper-header>

            <v-stepper-items>
              <v-stepper-content
                class="stepper-content"
                v-for="stepNumber in stepsLength"
                :key="`${stepNumber}-content`"
                :step="stepNumber"
              >
                <component
                  v-bind:is="getStepComponent(stepNumber)"
                  v-if="currentStep.step === stepsList[stepNumber - 1].step"
                  :current-step="currentStep"
                  :row-statuses="rowStatuses"
                  :table-columns="stepsList[stepNumber - 1].tableColumns"
                  @onUpdateStepStatus="updateStepStatus"
                />
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-col>
      </v-row>
      <v-row dense class="footer">
        <v-col cols="auto">
          <v-btn
            text
            color="accent"
            v-if="currentStepNumber > 1"
            @click="handlePrevStep"
          >
            Back
          </v-btn>
        </v-col>
        <v-spacer />
        <v-col cols="auto">
          <v-btn
            text
            color="success"
            v-if="currentStepNumber < stepsLength"
            @click="showCompletionDialog"
          >
            Next
          </v-btn>
          <v-btn
            text
            color="success"
            v-else-if="
              currentStepNumber === stepsLength &&
              currentStep.status !== StepsStatusesMap.COMPLETED &&
              currentStep.status !== StepsStatusesMap.SIGNED
            "
            @click="showCompletionDialog"
          >
            Complete
          </v-btn>
          <v-btn
            text
            color="success"
            v-else-if="
              currentStepNumber === stepsLength &&
              currentStep.status !== StepsStatusesMap.SIGNED
            "
            @click="openSignatureDialog"
          >
            Sign
          </v-btn>
        </v-col>
        <v-col
          cols="auto"
          v-if="currentStep.status === StepsStatusesMap.SIGNED"
        >
          <v-btn outlined color="primary" @click="openSignatureDialog">
            <v-icon left>mdi-clipboard-check</v-icon>
            Migration PAL signed
          </v-btn>
        </v-col>
      </v-row>
    </v-col>
    <SignatureDialog ref="signatureDialog" @onSigned="handleSigned" />
    <ConfirmationDialog
      ref="confirmationDialog"
      @onConfirm="handleStepCompletion"
    />
  </v-row>
</template>

<script>
import { Axios } from '@/services';
import { verifyToken } from '@/api';
import {
  DEFAULTS,
  StepsStatusesMap,
  StepsNamesMap,
  StepsList
} from '@/constants';
import MultimatchStep from '@/components/migration/MultimatchStep';
import SignatureDialog from '@/components/dialogs/SignatureDialog';
import CommonStep from '@/components/migration/CommonStep';
import IgnoredStep from '@/components/migration/IgnoredStep';
import ConfirmationDialog from '@/components/common/ConfirmationDialog';
import { migrationApiV1, rowStatusesV1 } from '@/api/v1';
import { migrationApiV2, rowStatusesV2 } from '@/api/v2';

const StepColorsByStatusMap = {
  [StepsStatusesMap.PENDING]: 'accent',
  [StepsStatusesMap.IN_PROGRESS]: 'secondary',
  [StepsStatusesMap.COMPLETED]: 'primary',
  [StepsStatusesMap.SIGNED]: 'success'
};
const StepsComponentsMap = {
  [StepsNamesMap.MULTI_MATCH]: MultimatchStep,
  [StepsNamesMap.IGNORED]: IgnoredStep,
  DEFAULT: CommonStep
};

export default {
  data: () => ({
    StepsStatusesMap,
    StepColorsByStatusMap,
    StepsComponentsMap,
    stepsList: StepsList,
    accessTokenHashId: null,
    result_id: null,
    currentStepNumber: 1,
    stepsLength: StepsList.length,
    existStepsMap: {},
    verified: false,
    rowStatuses: []
  }),
  components: {
    MultimatchStep,
    SignatureDialog,
    CommonStep,
    IgnoredStep,
    ConfirmationDialog
  },
  async mounted() {
    this.accessTokenHashId = this.$route.query.access_token;
    this.result_id = this.$route.params.result_id;
    this.verifyAccess().then(() => {
      this.verified = true;
      this.init();
    });
  },
  computed: {
    currentStep() {
      return this.stepsList[this.currentStepNumber - 1];
    },
    isLastStep() {
      return this.currentStepNumber === this.stepsLength;
    }
  },
  methods: {
    async verifyAccess() {
      const access_token = await verifyToken(
        this.result_id,
        this.accessTokenHashId,
        DEFAULTS.LINKS_TYPES_MAP.MIG_PAL_LINK
      );

      Axios.Api.setToken(access_token);
    },
    async getExistSteps() {
      const existSteps = await (
        this.$store.getters.isMatcherApiV2Enabled
          ? migrationApiV2
          : migrationApiV1
      ).getResultMigrationSteps(this.result_id);

      this.existStepsMap = existSteps.reduce(
        (stepsMap, step) => ({
          ...stepsMap,
          [step.step]: step
        }),
        {}
      );

      this.updateCurrentStepsStatuses();
    },
    getStepComponent(stepNumber) {
      return (
        StepsComponentsMap[this.stepsList[stepNumber - 1].step] ||
        StepsComponentsMap.DEFAULT
      );
    },
    updateCurrentStepsStatuses() {
      this.stepsList = this.stepsList.map(({ step, ...stepEl }) => ({
        step,
        ...stepEl,
        ...(this.existStepsMap[step] ? this.existStepsMap[step] : {})
      }));
    },
    async init() {
      await this.getExistSteps();
      await this.getRowStatuses();
    },
    showCompletionDialog() {
      const isStepCompleted = this.checkIsStepCompleted(this.currentStep);

      if (!isStepCompleted) {
        this.$refs.confirmationDialog.open('Complete step?', 'Complete');
      } else {
        this.handleNextStep();
      }
    },
    handleNextStep() {
      this.currentStepNumber++;
    },
    handlePrevStep() {
      this.currentStepNumber--;
    },
    async handleStepCompletion() {
      await this.updateStepStatus(StepsStatusesMap.COMPLETED);

      if (!this.isLastStep) {
        this.currentStepNumber++;
      } else {
        await this.openSignatureDialog();
      }
    },
    async updateStepStatus(statusName) {
      const { step } = this.currentStep;
      const isStepExist = this.existStepsMap[step];

      if (isStepExist) {
        await (
          this.$store.getters.isMatcherApiV2Enabled
            ? migrationApiV2
            : migrationApiV1
        ).updateMigrationStep(this.result_id, step, {
          status: statusName
        });
      } else {
        await (
          this.$store.getters.isMatcherApiV2Enabled
            ? migrationApiV2
            : migrationApiV1
        ).createMigrationStep(this.result_id, {
          step,
          status: statusName
        });
      }

      await this.getExistSteps();
    },
    checkIsStepCompleted(step) {
      return (
        step.status === StepsStatusesMap.COMPLETED ||
        this.checkIsStepSigned(step)
      );
    },
    checkIsStepSigned(step) {
      return step.status === StepsStatusesMap.SIGNED;
    },
    async openSignatureDialog() {
      this.$refs.signatureDialog.open(this.result_id, this.currentStep);
    },
    async handleSigned() {
      await this.getExistSteps();
    },
    handleOpenDashboard() {
      this.$router.push({
        path: 'dashboard',
        query: { access_token: this.accessTokenHashId }
      });
    },
    async getRowStatuses() {
      const { items } = await (
        this.$store.getters.isMatcherApiV2Enabled
          ? rowStatusesV2
          : rowStatusesV1
      ).fetchRowStatuses();

      this.rowStatuses = items;
    }
  }
};
</script>

<style lang="scss">
.table-wrapper {
  .v-stepper__content {
    padding: 0;
    height: calc(100vh - 256px);

    .v-stepper__wrapper {
      height: 100%;
    }
  }
}
.footer {
  height: 40px;
}
.step-container {
  display: flex;
  flex-direction: column;
  height: 100%;

  > * {
    flex-basis: 0;
  }
}
</style>
